import React, { useState, useEffect, useRef, useMemo } from "react"
import { ExcursionData, PlaceData } from "../../models"
import IconsColor from "util/iconsColor"
import styles from "./excursionPlaceDropdown.module.scss"

export type ExcursionPlaceItemType = "excursion" | "place"

export interface ExcursionPlaceOption {
    type: ExcursionPlaceItemType
    id: string
    name: string
    city_name: string
}

export interface ExcursionPlaceDropdownProps {
    excursions: ExcursionData[]
    places: PlaceData[]
    label?: string
    placeholder?: string
    value?: ExcursionPlaceOption | null
    onChange: (option: ExcursionPlaceOption | null) => void
    required?: boolean
    error?: string
    disabled?: boolean
    width?: string
    /**
     * Locked-city mode. When this prop is passed (even as `null`/`""`), the horizontal
     * city tabs are hidden and results are restricted to this single city:
     *  - non-empty string → show only that city's excursions/places (search still applies).
     *  - null / ""        → no city chosen yet; show the "select a city first" hint.
     * When the prop is `undefined`, the component keeps its legacy multi-city tabs behavior.
     */
    lockedCityName?: string | null
    /** When false, the Excursions section is hidden (places-only picker). Defaults to true. */
    showExcursions?: boolean
    /** When true, places are browsable across all cities (city tabs). Excursions follow lockedExcursionsCityName or lockedCityName. */
    showAllPlaces?: boolean
    /** Restrict excursions to this city while showAllPlaces is enabled. */
    lockedExcursionsCityName?: string | null
}

const ExcursionPlaceDropdown: React.FC<ExcursionPlaceDropdownProps> = ({
    excursions,
    places,
    label,
    placeholder = "Select end point",
    value,
    onChange,
    required = false,
    error,
    disabled = false,
    width = "100%",
    lockedCityName,
    showExcursions = true,
    showAllPlaces = false,
    lockedExcursionsCityName,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")
    const [selectedCity, setSelectedCity] = useState<string | null>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const isLockedCityMode = lockedCityName !== undefined && !showAllPlaces
    const hasLockedCity = Boolean(lockedCityName && String(lockedCityName).trim())
    const hasLockedExcursionsCity = Boolean(
        lockedExcursionsCityName && String(lockedExcursionsCityName).trim(),
    )
    const showCityTabs = !isLockedCityMode || showAllPlaces

    const cities = useMemo(() => {
        const citySet = new Set<string>()
        excursions.forEach((e) => e.city_name && citySet.add(e.city_name))
        places.forEach((p) => p.city_name && citySet.add(p.city_name))
        return Array.from(citySet).sort((a, b) => a.localeCompare(b))
    }, [excursions, places])

    const filteredCities = useMemo(() => {
        if (!searchQuery.trim()) return cities
        const q = searchQuery.toLowerCase().trim()
        return cities.filter((c) => c.toLowerCase().includes(q))
    }, [cities, searchQuery])

    const tabSelectedCity =
        selectedCity && filteredCities.includes(selectedCity)
            ? selectedCity
            : filteredCities[0] ?? null

    const activeCity = isLockedCityMode
        ? hasLockedCity
            ? String(lockedCityName)
            : null
        : tabSelectedCity

    const activePlaceCity = showAllPlaces ? tabSelectedCity : activeCity

    const activeExcursionCity =
        lockedExcursionsCityName !== undefined
            ? hasLockedExcursionsCity
                ? String(lockedExcursionsCityName)
                : null
            : activeCity

    const cityExcursions = useMemo(() => {
        if (!activeExcursionCity) return []
        let list = excursions.filter((e) => e.city_name === activeExcursionCity)
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim()
            list = list.filter((e) => e.excursion_name?.toLowerCase().includes(q))
        }
        return list
    }, [excursions, activeExcursionCity, searchQuery])

    const cityPlaces = useMemo(() => {
        if (!activePlaceCity) return []
        let list = places.filter((p) => p.city_name === activePlaceCity)
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim()
            list = list.filter((p) => p.place_name?.toLowerCase().includes(q))
        }
        return list
    }, [places, activePlaceCity, searchQuery])


    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen((prev) => !prev)
            if (!isOpen) {
                setSelectedCity(activePlaceCity ?? activeCity)
                setSearchQuery("")
            }
        }
    }

    const handleSelectCity = (city: string) => {
        setSelectedCity(city)
    }

    const handleSelectExcursion = (e: ExcursionData) => {
        if (e.flag_active === false) return
        onChange({
            type: "excursion",
            id: e.excursion_id,
            name: e.excursion_name,
            city_name: e.city_name,
        })
        setIsOpen(false)
        setSearchQuery("")
    }

    const handleSelectPlace = (p: PlaceData) => {
        if (p.flag_active === false) return
        onChange({
            type: "place",
            id: p.place_id,
            name: p.place_name,
            city_name: p.city_name,
        })
        setIsOpen(false)
        setSearchQuery("")
    }

    const displayValue = value ? value.name : ""

    return (
        <div
            className={styles.wrapper}
            style={{ width }}
            ref={wrapperRef}
        >
            {label && (
                <label className={styles.label}>
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}
            <div
                className={`${styles.control} ${isOpen ? styles.open : ""} ${error ? styles.error : ""} ${disabled ? styles.disabled : ""}`}
                onClick={handleToggle}
                role="button"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <span className={!displayValue ? styles.placeholder : ""}>
                    {displayValue || placeholder}
                </span>
                <div className={styles.chevron}>
                    <IconsColor
                        iconName="chevronDown"
                        width="20px"
                        height="20px"
                        fillColor="none"
                        strokeColor="currentColor"
                    />
                </div>
            </div>

            {isOpen && (
                <div className={styles.dropdown}>
                    <div className={styles.searchWrap}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search locations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            autoFocus
                        />
                    </div>
                    {showCityTabs && (
                        <div className={styles.cityTabs}>
                            {filteredCities.map((city) => (
                                <button
                                    key={city}
                                    type="button"
                                    className={`${styles.cityTab} ${city === activePlaceCity ? styles.cityTabActive : ""}`}
                                    onClick={() => handleSelectCity(city)}
                                >
                                    {city}
                                </button>
                            ))}
                        </div>
                    )}
                    {isLockedCityMode && !hasLockedCity ? (
                        <div className={styles.results}>
                            <div className={styles.noItems}>
                                Please select a city first to view excursions and places
                            </div>
                        </div>
                    ) : (
                    <div className={styles.results}>
                        <>
                            {showExcursions && (
                                <div className={styles.section}>
                                    <div className={styles.sectionHeader}>
                                        <IconsColor
                                            iconName="excursions"
                                            width="16px"
                                            height="16px"
                                            fillColor="none"
                                            strokeColor="#dc3545"
                                        />
                                        <span>Excursions</span>
                                    </div>
                                    <ul className={styles.itemList} role="listbox">
                                        {cityExcursions.length === 0 ? (
                                            <li className={styles.noItems}>No excursions</li>
                                        ) : (
                                            cityExcursions.map((e) => (
                                                <li
                                                    key={e.excursion_id}
                                                    role="option"
                                                    className={`${styles.item} ${e.flag_active === false ? styles.itemDisabled : ""}`}
                                                    onClick={() => handleSelectExcursion(e)}
                                                    aria-disabled={e.flag_active === false}
                                                    aria-selected={value?.type === "excursion" && value?.id === e.excursion_id}
                                                >
                                                    {e.excursion_name}
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                </div>
                            )}
                                <div className={styles.section}>
                                    <div className={styles.sectionHeader}>
                                        <IconsColor
                                            iconName="places"
                                            width="16px"
                                            height="16px"
                                            fillColor="none"
                                            strokeColor="#dc3545"
                                        />
                                        <span>Places</span>
                                    </div>
                                    <ul className={styles.itemList} role="listbox">
                                        {cityPlaces.length === 0 ? (
                                            <li className={styles.noItems}>No places</li>
                                        ) : (
                                            cityPlaces.map((p) => (
                                                <li
                                                    key={p.place_id}
                                                    role="option"
                                                    className={`${styles.item} ${p.flag_active === false ? styles.itemDisabled : ""}`}
                                                    onClick={() => handleSelectPlace(p)}
                                                    aria-disabled={p.flag_active === false}
                                                    aria-selected={value?.type === "place" && value?.id === p.place_id}
                                                >
                                                    {p.place_name}
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                </div>
                        </>
                    </div>
                    )}
                </div>
            )}

            {error && (
                <span className={styles.errorText}>{error}</span>
            )}
        </div>
    )
}

export default ExcursionPlaceDropdown
